/**
 * MONSTER TOWN — PARTICLES / VFX. OWNED BY THE INTERIORS CREW.
 *
 * The `fx:play` half of LAW #1: bubbles off the bath, steam off the stove, zzz
 * off the bed, petals off a flower, coins off a job. Without this, half of
 * every `InteractionResult` in the game is invisible.
 *
 * OLDER-IPAD RULES BAKED IN
 *  - TWO draw calls for the WHOLE game's particles, ever. Everything is two
 *    `InstancedMesh`es (a chip and a puff) with per-instance colour; nothing is
 *    allocated per burst and nothing is created per prop.
 *  - Hard cap on live particles. A kid hammering the bath tap cannot make the
 *    frame budget worse — the oldest particle is recycled instead.
 *  - Unlit `MeshBasicMaterial`, no shadows, no depth writes fighting the toon
 *    world.
 *  - Counts scale down automatically on the low quality tier.
 *
 * ADDING AN EFFECT IS A DATA DROP into `EFFECTS`. Never an engine change.
 */

import * as THREE from 'three'
import { bus } from '../core/events'
import { engine } from '../core/engine'
import { hex, type PaletteName } from '../core/palette'
import { world } from '../core/scene'
import type { Vec3 } from '../core/types'

/**
 * Hard ceiling, per instanced mesh. Measured on the `ipad-old` profile (4x CPU
 * throttle): a sustained storm of ~70 new particles every 250ms — far past
 * anything a kid can produce through taps — sat at work p50 12ms against a
 * 14ms budget. 128 keeps headroom for the town crew's scene on top.
 */
const MAX_PARTICLES = 128
const MAX_PER_BURST = 12

type Shape = 'chip' | 'puff'

interface EffectSpec {
  readonly colors: readonly PaletteName[]
  readonly shape: Shape
  /** World units. Scaled by the grid the burst happens in. */
  readonly size: number
  /** Initial outward speed. */
  readonly speed: number
  /** Extra upward kick on top of `speed`. */
  readonly rise: number
  /** Negative pulls down. */
  readonly gravity: number
  readonly life: number
  readonly spin: number
  /** Vertical offset from the interaction point. */
  readonly up: number
  /** Spread as a fraction of a sphere: 1 = every direction, 0.2 = a narrow jet. */
  readonly spread: number
}

const D = {
  shape: 'chip' as Shape,
  size: 0.09,
  speed: 1.5,
  rise: 1.1,
  gravity: -2.2,
  life: 0.85,
  spin: 5,
  up: 0.5,
  spread: 1,
}

/**
 * THE EFFECT TABLE. Every `{ kind:'particles', effect:'x' }` a placeable can
 * return must have an entry here or it falls back to `sparkle` — never to
 * nothing.
 */
const EFFECTS: Record<string, EffectSpec> = {
  sparkle: { ...D, colors: ['flowerYellow', 'flowerWhite', 'uiCoin'], size: 0.08, life: 0.8 },
  stars: { ...D, colors: ['uiCoin', 'flowerYellow', 'flowerWhite'], size: 0.11, speed: 2, rise: 1.8 },
  dust: { ...D, colors: ['path', 'pathEdge', 'sand'], shape: 'puff', size: 0.1, speed: 1, rise: 0.5, life: 0.6 },
  dustPuff: { ...D, colors: ['path', 'sand', 'rockDark'], shape: 'puff', size: 0.13, speed: 1.2, rise: 0.6 },
  poof: { ...D, colors: ['wallCream', 'uiPanelAlt', 'sand'], shape: 'puff', size: 0.16, speed: 1.1, rise: 0.7, gravity: -0.6, life: 0.7 },
  petals: { ...D, colors: ['flowerPink', 'flowerWhite', 'flowerPurple', 'flowerRed'], size: 0.09, speed: 1.1, rise: 1.3, gravity: -1.1, life: 1.4, spin: 7 },
  leaves: { ...D, colors: ['leafLight', 'leaf', 'leafDark'], size: 0.1, speed: 1, rise: 1.2, gravity: -1, life: 1.3, spin: 6 },
  bubbles: { ...D, colors: ['glass', 'glassDark', 'water'], shape: 'puff', size: 0.13, speed: 0.8, rise: 1.9, gravity: 0.5, life: 1.5, spin: 2, spread: 0.5 },
  steam: { ...D, colors: ['uiPanel', 'wallCream', 'glass'], shape: 'puff', size: 0.15, speed: 0.5, rise: 1.6, gravity: 0.9, life: 1.3, spin: 1.5, spread: 0.35, up: 0.75 },
  splash: { ...D, colors: ['water', 'waterDeep', 'glass'], size: 0.09, speed: 2.1, rise: 1.6, gravity: -3.4, life: 0.7 },
  hearts: { ...D, colors: ['uiHeart', 'flowerPink', 'blush'], size: 0.14, speed: 0.6, rise: 1.7, gravity: 0.4, life: 1.5, spin: 3, spread: 0.4, up: 0.9 },
  zzz: { ...D, colors: ['uiPanel', 'glass', 'wallpaperBlue'], size: 0.13, speed: 0.35, rise: 1.1, gravity: 0.35, life: 1.9, spin: 2, spread: 0.3, up: 0.95 },
  notes: { ...D, colors: ['uiAccent', 'monsterLilac', 'flowerPurple'], size: 0.12, speed: 0.8, rise: 1.5, gravity: 0.3, life: 1.5, spin: 4, spread: 0.5, up: 0.85 },
  confetti: { ...D, colors: ['flowerPink', 'uiCoin', 'uiAccent', 'monsterLime', 'flowerPurple'], size: 0.1, speed: 2.4, rise: 2.4, gravity: -2.8, life: 1.5, spin: 9 },
  coins: { ...D, colors: ['uiCoin', 'uiCoinDark', 'flowerYellow'], size: 0.12, speed: 1.3, rise: 2.2, gravity: -3, life: 1, spin: 8 },
  crumbs: { ...D, colors: ['cone', 'wood', 'iceChocolate'], size: 0.07, speed: 1.4, rise: 0.9, gravity: -3, life: 0.7 },
  sizzle: { ...D, colors: ['uiWarn', 'uiPrimary', 'flowerYellow'], size: 0.07, speed: 1.4, rise: 1.5, gravity: -1.4, life: 0.6, spread: 0.6 },
  paint: { ...D, colors: ['wallpaperMint', 'wallpaperBlue', 'wallPink', 'wallLilac', 'wallCream'], size: 0.11, speed: 1.6, rise: 1.6, gravity: -2, life: 1.1, spin: 6 },
  soap: { ...D, colors: ['uiPanel', 'glass', 'wallpaperBlue'], shape: 'puff', size: 0.14, speed: 0.7, rise: 1.4, gravity: 0.6, life: 1.6, spin: 2, spread: 0.6 },
  snow: { ...D, colors: ['uiPanel', 'flowerWhite', 'glass'], size: 0.08, speed: 0.4, rise: 0.6, gravity: -0.5, life: 2, spin: 3 },

  // Found by the cross-crew wiring gate: emitted by content, never defined, so
  // these bursts were resolving to the fallback instead of the thing the prop
  // meant. Adding an effect is a data line — so here are the three lines.
  /** The coin-shaped twinkle a job marker and a shop plinth ask for. */
  coinSparkle: { ...D, colors: ['uiCoin', 'uiCoinDark', 'flowerYellow', 'flowerWhite'], size: 0.09, speed: 0.9, rise: 1.6, gravity: -1.2, life: 1, spin: 6 },
  /** Cold air off an open ice cream tub. */
  frost: { ...D, colors: ['glass', 'glassDark', 'wallpaperBlue', 'uiPanel'], shape: 'puff', size: 0.11, speed: 0.6, rise: 1.1, gravity: 0.7, life: 1.2, spread: 0.5, up: 0.8 },
  /** Singular of `notes` — a jukebox that pops one note, not a chord. */
  note: { ...D, colors: ['uiAccent', 'monsterLilac'], size: 0.13, speed: 0.6, rise: 1.4, gravity: 0.3, life: 1.4, spin: 4, spread: 0.35, up: 0.9 },
}

export function effectNames(): string[] {
  return Object.keys(EFFECTS)
}

// ---------------------------------------------------------------------------
// pool
// ---------------------------------------------------------------------------

interface Particle {
  x: number
  y: number
  z: number
  vx: number
  vy: number
  vz: number
  life: number
  maxLife: number
  size: number
  gravity: number
  spin: number
  rot: number
  shape: Shape
  color: number
}

const live: Particle[] = []
const free: Particle[] = []

function take(): Particle {
  const p = free.pop()
  if (p !== undefined) return p
  return {
    x: 0,
    y: 0,
    z: 0,
    vx: 0,
    vy: 0,
    vz: 0,
    life: 0,
    maxLife: 1,
    size: 0.1,
    gravity: -2,
    spin: 0,
    rot: 0,
    shape: 'chip',
    color: 0xffffff,
  }
}

// ---------------------------------------------------------------------------
// meshes — TWO for the entire game
// ---------------------------------------------------------------------------

let chipMesh: THREE.InstancedMesh | null = null
let puffMesh: THREE.InstancedMesh | null = null
let root: THREE.Group | null = null
const dummy = new THREE.Object3D()
const tint = new THREE.Color()

function build(): void {
  if (root !== null) return
  root = new THREE.Group()
  root.name = 'fx'
  // These two materials are created ONCE for the whole game — the "never new
  // Material in a prop" rule is about art builders that run hundreds of times.
  const mat = new THREE.MeshBasicMaterial({ toneMapped: false })
  const chipGeo = new THREE.BoxGeometry(1, 1, 0.4)
  const puffGeo = new THREE.OctahedronGeometry(0.62, 0)
  chipMesh = new THREE.InstancedMesh(chipGeo, mat, MAX_PARTICLES)
  puffMesh = new THREE.InstancedMesh(puffGeo, mat, MAX_PARTICLES)
  for (const m of [chipMesh, puffMesh]) {
    m.frustumCulled = false
    m.castShadow = false
    m.receiveShadow = false
    m.count = 0
    m.instanceMatrix.setUsage(THREE.DynamicDrawUsage)
    // Per-instance colour: one material, twenty different sparkle colours.
    m.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(MAX_PARTICLES * 3), 3)
    m.instanceColor.setUsage(THREE.DynamicDrawUsage)
    root.add(m)
  }
}

/** Park the particle root in whichever scene is awake. */
function attach(): void {
  build()
  const id = world.activeSceneId
  if (id === null || root === null) return
  const node = world.get(id)
  if (node === undefined) return
  if (root.parent !== node.actors) node.actors.add(root)
}

// ---------------------------------------------------------------------------
// spawning
// ---------------------------------------------------------------------------

let quality: 'high' | 'medium' | 'low' = 'high'

/** The renderer drops quality before it drops frames; particles follow. */
export function setFxQuality(tier: 'high' | 'medium' | 'low'): void {
  quality = tier
}

/**
 * Fire a burst. Safe to call with an unknown effect name (falls back) and safe
 * to spam — over the cap, the oldest particles are recycled rather than the
 * frame budget being blown. LAW #2: a kid can never do this "wrong".
 */
export function burst(effect: string, at: Vec3, count = 6, scale = 1): void {
  const spec = EFFECTS[effect] ?? EFFECTS['sparkle']
  if (spec === undefined) return
  attach()

  const mult = quality === 'low' ? 0.5 : quality === 'medium' ? 0.75 : 1
  let n = Math.max(1, Math.min(MAX_PER_BURST, Math.round(count * mult)))

  while (live.length + n > MAX_PARTICLES && live.length > 0) {
    const oldest = live.shift()
    if (oldest !== undefined) free.push(oldest)
  }
  n = Math.min(n, MAX_PARTICLES - live.length)

  const rng = engine.rng
  for (let i = 0; i < n; i++) {
    const p = take()
    const theta = rng() * Math.PI * 2
    const cone = 1 - spec.spread * rng()
    const horiz = Math.sqrt(Math.max(0, 1 - cone * cone)) * spec.spread
    p.x = at.x + (rng() - 0.5) * spec.size * 2.5 * scale
    p.y = at.y + spec.up * scale + rng() * 0.1
    p.z = at.z + (rng() - 0.5) * spec.size * 2.5 * scale
    p.vx = Math.cos(theta) * horiz * spec.speed * scale
    p.vz = Math.sin(theta) * horiz * spec.speed * scale
    p.vy = (spec.rise + rng() * spec.rise * 0.5) * scale
    p.gravity = spec.gravity * scale
    p.maxLife = spec.life * (0.75 + rng() * 0.5)
    p.life = p.maxLife
    p.size = spec.size * (0.7 + rng() * 0.6) * scale
    p.spin = (rng() - 0.5) * spec.spin * 2
    p.rot = rng() * Math.PI
    p.shape = spec.shape
    const pick = spec.colors[Math.floor(rng() * spec.colors.length)] ?? spec.colors[0]
    p.color = pick === undefined ? 0xffffff : hex(pick)
    live.push(p)
  }
}

// ---------------------------------------------------------------------------
// install
// ---------------------------------------------------------------------------

export function installFx(): void {
  bus.on('fx:play', (e) => {
    burst(e.effect, e.at, e.count)
  })

  // Coins are always worth a little shower wherever they were earned.
  bus.on('economy:earned', (e) => {
    if (e.at === undefined) return
    burst('coins', e.at, Math.min(8, 3 + Math.round(e.amount / 4)))
  })

  // Placing and picking up pieces should feel physical, not silent.
  bus.on('build:placed', (e) => {
    const node = world.get(e.sceneId)
    if (node === undefined) return
    const p = e.placement
    const c = node.grid.footprintCenter(p.anchor, p.footprint, p.rot)
    burst('dustPuff', c, 5, node.grid.tileSize)
  })

  bus.on('scene:left', () => clear())

  engine.addSystem({
    id: 'interactions.fx',
    order: 72,
    fixed: (dt) => stepSim(dt),
    render: () => paint(),
  })
}

export function clear(): void {
  for (const p of live) free.push(p)
  live.length = 0
  if (chipMesh !== null) chipMesh.count = 0
  if (puffMesh !== null) puffMesh.count = 0
}

function stepSim(dt: number): void {
  if (live.length === 0) return
  for (let i = live.length - 1; i >= 0; i--) {
    const p = live[i]
    if (p === undefined) continue
    p.life -= dt
    if (p.life <= 0) {
      live.splice(i, 1)
      free.push(p)
      continue
    }
    p.vy += p.gravity * dt
    p.x += p.vx * dt
    p.y += p.vy * dt
    p.z += p.vz * dt
    p.rot += p.spin * dt
    // Gentle drag so nothing flies off the plot.
    p.vx *= 0.97
    p.vz *= 0.97
  }
}

function paint(): void {
  if (chipMesh === null || puffMesh === null) return
  if (live.length === 0) {
    if (chipMesh.count !== 0 || puffMesh.count !== 0) {
      chipMesh.count = 0
      puffMesh.count = 0
    }
    return
  }
  attach()
  let chips = 0
  let puffs = 0
  for (const p of live) {
    const mesh = p.shape === 'puff' ? puffMesh : chipMesh
    const idx = p.shape === 'puff' ? puffs++ : chips++
    if (idx >= MAX_PARTICLES) continue
    // Fade by shrinking: no per-particle transparency sorting on an old iPad.
    const u = Math.max(0, Math.min(1, p.life / p.maxLife))
    const s = p.size * (0.35 + u * 0.65)
    dummy.position.set(p.x, p.y, p.z)
    dummy.rotation.set(p.rot * 0.6, p.rot, p.rot * 0.35)
    dummy.scale.set(s, s, s)
    dummy.updateMatrix()
    mesh.setMatrixAt(idx, dummy.matrix)
    tint.setHex(p.color)
    mesh.setColorAt(idx, tint)
  }
  chipMesh.count = chips
  puffMesh.count = puffs
  chipMesh.instanceMatrix.needsUpdate = true
  puffMesh.instanceMatrix.needsUpdate = true
  if (chipMesh.instanceColor !== null) chipMesh.instanceColor.needsUpdate = true
  if (puffMesh.instanceColor !== null) puffMesh.instanceColor.needsUpdate = true
}

/** Debug/harness: how many particles are alive. */
export function liveParticleCount(): number {
  return live.length
}
